import { useSignal } from "@preact/signals";
import { useEffect } from "preact/hooks";
import { toast } from "sonner";
import type { ApiKeyInfo } from "~/protocol/keys";
import { createApiKey, listApiKeys, revokeApiKey } from "~/web/src/api";
import { setClipboard } from "~/web/src/components/clipboard";
import { errorText } from "~/web/src/errors";
import { stamp } from "~/web/src/format";
import { Topbar } from "~/web/src/topbar";

// /api-keys: the org's keys for the API and the MCP server (spec: api-spec.md, "Keys"). Any
// member mints one; the token shows once, in a copy box, and the row lists its creator, its
// birth and its last use until it is revoked. The page carries the two connection lines and
// points agents at /skill.md.

export function ApiKeysPage() {
  const keys = useSignal<ApiKeyInfo[] | null>(null);
  const error = useSignal<string | null>(null);
  const name = useSignal("");
  const busy = useSignal(false);
  const minted = useSignal<{ name: string; token: string } | null>(null);

  const load = async () => {
    try {
      keys.value = await listApiKeys();
      error.value = null;
    } catch (err) {
      error.value = `Couldn't load the keys: ${errorText(err)}`;
    }
  };
  useEffect(() => {
    void load();
  }, []);

  async function create(e: Event) {
    e.preventDefault();
    const n = name.value.trim();
    if (!n || busy.value) return;
    busy.value = true;
    try {
      const made = await createApiKey(n);
      minted.value = { name: made.key.name, token: made.token };
      name.value = "";
      await load();
    } catch (err) {
      error.value = `Couldn't create the key: ${errorText(err)}`;
    } finally {
      busy.value = false;
    }
  }

  async function revoke(key: ApiKeyInfo) {
    if (!confirm(`Revoke "${key.name}"? Every call made with it stops working at once.`)) return;
    try {
      await revokeApiKey(key.id);
      if (minted.value?.name === key.name) minted.value = null;
      await load();
    } catch (err) {
      error.value = `Couldn't revoke the key: ${errorText(err)}`;
    }
  }

  async function copy(text: string) {
    try {
      await setClipboard(text);
      toast.success("Copied");
    } catch (err) {
      error.value = `Couldn't copy: ${errorText(err)}`;
    }
  }

  const origin = location.origin;
  const claudeLine = `claude mcp add --transport http featurette ${origin}/mcp --header "Authorization: Bearer <key>"`;
  const k = keys.value;
  return (
    <div class="app">
      <Topbar name="API keys" />
      <main class="home">
        <section class="keys">
          <h1>API keys</h1>
          <p class="dim">
            A key is your org's identity on the API and the MCP server: whoever holds it can start
            videos, steer their directors and spend your org's credits. Any member can create one;
            revoking it stops every call at once.
          </p>

          <h2>Connecting</h2>
          <div class="box">
            <span class="dim">MCP server, for claude.ai connectors and Claude Code</span>
            <pre>{origin}/mcp</pre>
            <span class="dim">
              In claude.ai: a custom connector with that URL, “No sign-in”, and the request header{" "}
              <code>Authorization</code> = <code>Bearer &lt;key&gt;</code>. In Claude Code:
            </span>
            <pre>{claudeLine}</pre>
            <span class="dim">HTTP API, the same header</span>
            <pre>{origin}/api/v1</pre>
            <span class="dim">
              The skill that describes both, for agents: <a href="/skill.md">{origin}/skill.md</a>
            </span>
          </div>

          <h2>Create a key</h2>
          <form onSubmit={(e) => void create(e)}>
            <input
              type="text"
              placeholder="What it is for (a Clay table, a Claude)"
              value={name.value}
              onInput={(e) => {
                name.value = (e.currentTarget as HTMLInputElement).value;
              }}
              disabled={busy.value}
            />
            <button type="submit" class="btn" disabled={busy.value || !name.value.trim()}>
              Create key
            </button>
          </form>
          {minted.value && (
            <div class="box token" aria-live="polite">
              <span class="dim">
                The key “{minted.value.name}”. It is shown this once: copy it now.
              </span>
              <div class="row">
                <pre>{minted.value.token}</pre>
                <button
                  type="button"
                  class="btn ghost sm"
                  onClick={() => void copy(minted.value?.token ?? "")}
                >
                  Copy
                </button>
              </div>
            </div>
          )}

          <h2>Keys</h2>
          {!k && !error.value && <span class="dim">loading…</span>}
          {k && k.length === 0 && <span class="dim">No keys yet.</span>}
          {k && k.length > 0 && (
            <table>
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Created by</th>
                  <th>Created</th>
                  <th>Last used</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {k.map((key) => (
                  <tr key={key.id} class={key.revokedAt ? "revoked" : undefined}>
                    <td>{key.name}</td>
                    <td>{key.createdBy}</td>
                    <td>{stamp(key.createdAt)}</td>
                    <td>
                      {key.revokedAt ? `revoked ${stamp(key.revokedAt)}` : stamp(key.lastUsedAt)}
                    </td>
                    <td>
                      {!key.revokedAt && (
                        <button type="button" class="btn ghost sm" onClick={() => void revoke(key)}>
                          Revoke
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          {error.value && (
            <div class="error" role="alert">
              {error.value}
            </div>
          )}
        </section>
      </main>
    </div>
  );
}
